import { useEffect, useRef, type RefObject } from 'react';

export interface UseLoadWhenVisibleOptions {
  /** When false, the observer is disconnected and no load is triggered. */
  enabled: boolean;
  /** Ref that returns whether more can be loaded (e.g. hasMore). Read at trigger time. */
  canLoadRef: RefObject<boolean>;
  /** Ref to the load callback (e.g. onLoadMore). Called when element is visible and canLoad. */
  onLoadRef: RefObject<(() => void) | undefined>;
  /** rootMargin for IntersectionObserver (e.g. "100px" to trigger before fully visible). */
  rootMargin?: string;
  /** threshold for IntersectionObserver (0–1). Default 0. */
  threshold?: number;
}

/**
 * Reusable hook: when the observed element enters the viewport (and canLoad is true),
 * calls the load callback once. Resets when the element leaves the viewport so it can
 * trigger again on next entry. Use with button/sentinel refs for infinite scroll.
 *
 * Pass the current element (e.g. from state updated by a callback ref) so the effect
 * re-runs when the sentinel is mounted/unmounted (e.g. when hasMore/hasPrev toggles).
 */
export function useLoadWhenVisible(
  element: HTMLElement | null,
  options: UseLoadWhenVisibleOptions
): void {
  const {
    enabled,
    canLoadRef,
    onLoadRef,
    rootMargin = '100px',
    threshold = 0
  } = options;

  const triggeredForCurrentEntryRef = useRef(false);

  useEffect(() => {
    if (!element || !enabled) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          if (
            canLoadRef.current &&
            typeof onLoadRef.current === 'function' &&
            !triggeredForCurrentEntryRef.current
          ) {
            triggeredForCurrentEntryRef.current = true;
            onLoadRef.current();
          }
        } else {
          triggeredForCurrentEntryRef.current = false;
        }
      },
      { root: null, rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
    // canLoadRef and onLoadRef are refs: read in callback, no need in deps
  }, [enabled, element, rootMargin, threshold]);
}
